import React, { useEffect, useState, useRef } from 'react';
import { useParams, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useShopContext } from '../../context/ShopContext';
import { Helmet } from 'react-helmet-async';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useTranslation } from 'react-i18next';
import { useCart } from '../../context/CartContext';
import { ShoppingCart } from '@mui/icons-material';
import { useAutoPageTracking } from '../../utils/analyticsAutoTracker';

const StoreLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { stores, selectedStore, setSelectedStore, loading } = useShopContext();
  const { t } = useTranslation();
  useAutoPageTracking();

  const [user, setUser] = useState<{ [key: string]: any } | null>(null);
  const mainContentRef = useRef<HTMLDivElement>(null);

  // Scroll to top when route changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (mainContentRef.current) {
      mainContentRef.current.scrollTop = 0;
    }
  }, [location.pathname]);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch {
        setUser(null);
      }
    }
  }, []);

  const { cartItems } = useCart();
  const cartCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  const togglePreview = () => {
    if (user) navigate('/cart');
    else navigate('/login');
  };

  // تعيين المتجر المحدد بناءً على slug إذا موجود
  useEffect(() => {
    if (!loading && stores.length > 0 && slug) {
      // حاول إيجاد المتجر باستخدام slug أولاً
      const foundStoreBySlug = stores.find((store) => store.slug === slug);
      if (foundStoreBySlug) {
        setSelectedStore(foundStoreBySlug);
        return;
      }
      
      // إذا لم يوجد بـ slug، جرب البحث بـ name (للتوافق مع الكود القديم)
      const foundStoreByName = stores.find((store) => store.name === slug);
      if (foundStoreByName) {
        setSelectedStore(foundStoreByName);
        return;
      }
    }
  }, [slug, stores, loading, setSelectedStore]);

  // تطبيق الثيم
  useEffect(() => {
    if (selectedStore?.theme) {
      const theme = selectedStore.theme;
      const root = document.documentElement;
      
      // 🔴 الخلفية = primary-color
      root.style.setProperty('--primary-color', theme.primary_color);
      
      // ⚪ النص = secondary-color
      root.style.setProperty('--secondary-color', theme.secondary_color);
      
      // Apply additional theme colors if available
      if (theme.button_primary_color) {
        root.style.setProperty('--button-primary-color', theme.button_primary_color);
      }
      if (theme.button_secondary_color) {
        root.style.setProperty('--button-secondary-color', theme.button_secondary_color);
      }
      if (theme.base_section_bg) {
        root.style.setProperty('--base-section-bg', theme.base_section_bg);
      }
      if (theme.secondary_section_bg) {
        root.style.setProperty('--secondary-section-bg', theme.secondary_section_bg);
      }
      
      // Apply background images based on screen size
      const setBackground = () => {
        if (window.innerWidth >= 768) {
          // Desktop
          if (theme.background_image_desktop) {
            document.body.style.backgroundImage = `url('${theme.background_image_desktop}')`;
            document.body.style.backgroundSize = 'cover';
            document.body.style.backgroundAttachment = 'fixed';
            document.body.style.backgroundPosition = 'center';
          } else {
            document.body.style.backgroundImage = '';
          }
        } else {
          // Mobile
          if (theme.background_image_mobile) {
            document.body.style.backgroundImage = `url('${theme.background_image_mobile}')`;
            document.body.style.backgroundSize = 'cover';
            document.body.style.backgroundAttachment = 'scroll';
            document.body.style.backgroundPosition = 'center';
          } else if (theme.background_image_desktop) {
            document.body.style.backgroundImage = `url('${theme.background_image_desktop}')`;
            document.body.style.backgroundSize = 'cover';
            document.body.style.backgroundAttachment = 'scroll';
          } else {
            document.body.style.backgroundImage = '';
          }
        }
      };

      setBackground();
      window.addEventListener('resize', setBackground);
      
      return () => {
        window.removeEventListener('resize', setBackground);
        document.body.style.backgroundImage = '';
        document.body.style.backgroundColor = '';
      };
    }
  }, [selectedStore]);

  // Loader أثناء انتظار البيانات
  if (loading || stores.length === 0 || !selectedStore) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-b from-gray-50 to-white">
        <div className="relative">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-8 h-8 border-2 border-secondary border-t-transparent rounded-full animate-spin"></div>
          </div>
        </div>
        <p className="mt-4 text-gray-600 font-medium">جارٍ التحميل...</p>
      </div>
    );
  }

  // التحقق من أن المتجر المحدد يتطابق مع الـ slug
  // نستخدم تحقق أكثر مرونة لتجنب المشاكل
  const isStoreValid = selectedStore.slug === slug || selectedStore.name === slug;
  
  if (!isStoreValid) {
    console.warn(`Store validation failed: slug=${slug}, store.slug=${selectedStore.slug}, store.name=${selectedStore.name}`);
    // بدلاً من إعادة التوجيه مباشرة، يمكنك عرض رسالة أو البحث عن متجر آخر
    // return <Navigate to="/" replace />;
  }

  return (
    <>
      <Helmet>
        <title>{selectedStore.name} | متجرك</title>
        <meta name="description" content={`تسوق منتجات ${selectedStore.name} عبر موقعنا`} />
        <meta property="og:image" content={selectedStore.theme?.logo || ''} />
      </Helmet>

      {/* Back to Top Button */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="fixed bottom-6 right-6 bg-[var(--primary-color)] text-[var(--secondary-color)] p-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 z-50 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
        aria-label="العودة إلى الأعلى"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
        </svg>
      </button>

      {/* Floating Cart Button for Mobile */}
      <button
        onClick={togglePreview}
        className="fixed bottom-6 left-6 bg-[var(--primary-color)] text-[var(--secondary-color)] p-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 z-50 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-secondary focus:ring-offset-2 md:hidden"
        aria-label={`عرض السلة (${cartCount} عنصر)`}
      >
        <div className="relative">
          <ShoppingCart className="w-6 h-6" />
          {cartCount > 0 && (
            <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
              {cartCount}
            </span>
          )}
        </div>
      </button>

      {/* Navbar */}
      <header className="bg-[var(--primary-color)] text-[var(--secondary-color)] shadow-lg sticky top-0 z-40 transition-all duration-300">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between py-3">
            {/* Left Section */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate(-1)}
                className="flex items-center gap-1 bg-white bg-opacity-20 px-3 py-2 rounded-lg hover:bg-opacity-30 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[var(--primary-color)]"
                aria-label={t('back')}
              >
                <ArrowBackIcon className="text-[var(--secondary-color)]" fontSize="small" />
                <span className="text-sm font-medium hidden sm:inline text-[var(--secondary-color)]">{t('back')}</span>
              </button>

              {selectedStore?.theme?.logo && (
                <div className="flex items-center gap-3">
                  <img
                    src={selectedStore.theme.logo}
                    alt={selectedStore.name}
                    className="h-10 w-10 rounded-lg object-cover border-2 border-[var(--secondary-color)] shadow-md"
                  />
                  <h1 className="text-lg font-bold hidden md:block truncate max-w-xs text-[var(--secondary-color)]">
                    {selectedStore.name}
                  </h1>
                </div>
              )}
            </div>

            {/* Center - Store Name for Mobile */}
            <div className="absolute left-1/2 transform -translate-x-1/2 md:hidden">
              <h1 className="text-base font-bold truncate max-w-[200px] text-[var(--secondary-color)]">
                {selectedStore.name}
              </h1>
            </div>

            {/* Right Section */}
            <div className="flex items-center gap-4">
              {/* Cart Button for Desktop */}
              <button
                onClick={togglePreview}
                className="hidden md:flex items-center gap-2 bg-white bg-opacity-20 px-4 py-2 rounded-lg hover:bg-opacity-30 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[var(--primary-color)]"
                aria-label={`عربة التسوق (${cartCount} عنصر)`}
              >
                <div className="relative">
                  <ShoppingCart className="text-[var(--secondary-color)]" />
                  {cartCount > 0 && (
                    <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                      {cartCount}
                    </span>
                  )}
                </div>
                <span className="text-sm font-medium text-[var(--secondary-color)]">السلة</span>
              </button>

              {/* Branch Count Badge */}
              <div className="hidden sm:flex items-center gap-2 px-3 py-1 bg-white bg-opacity-20 rounded-full">
                <span className="text-xs font-medium text-[var(--secondary-color)]">🏪 {selectedStore.branches.length} فرع</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main 
        ref={mainContentRef}
        className="pb-8 min-h-screen bg-gradient-to-b from-white to-[var(--secondary-section-bg, #f8f9fa)]"
      >
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-[var(--primary-color)] text-[var(--secondary-color)] mt-auto">
        {/* Decorative Top Border */}
        <div className="h-1 bg-gradient-to-r from-[var(--secondary-color)] to-[var(--primary-color)]"></div>
        
        <div className="container mx-auto px-4 py-10">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Store Info */}
            <div className="space-y-4">
              <div className="flex flex-col items-center sm:items-start">
                {selectedStore.theme?.logo && (
                  <img 
                    src={selectedStore.theme.logo} 
                    alt={selectedStore.name} 
                    className="h-20 w-20 rounded-xl object-cover border-3 border-[var(--secondary-color)] shadow-lg mb-4"
                  />
                )}
                <h2 className="text-2xl font-bold mb-2 text-center sm:text-left text-[var(--secondary-color)]">{selectedStore.name}</h2>
                <p className="text-gray-200 text-sm">
                  منذ {new Date(selectedStore.created_at).getFullYear()} - جميع الحقوق محفوظة © {new Date().getFullYear()}
                </p>
                <div className="mt-3 inline-flex items-center gap-2 px-3 py-1 bg-white bg-opacity-20 rounded-full">
                  <span className="text-xs font-medium text-[var(--secondary-color)]">🏪 {selectedStore.branches.length} فروع</span>
                </div>
              </div>
            </div>

            {/* Contact Info */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold mb-4 text-center sm:text-left text-[var(--secondary-color)]">تواصل مع المالك مباشرة</h3>
              
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 bg-white bg-opacity-10 rounded-xl">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 rounded-full bg-white bg-opacity-20 flex items-center justify-center">
                      <span className="text-lg text-[var(--secondary-color)]">👤</span>
                    </div>
                  </div>
                  <div>
                    <p className="font-medium text-[var(--secondary-color)]">
                      {selectedStore.owner.first_name || ''} {selectedStore.owner.last_name || selectedStore.owner.username}
                    </p>
                    <p className="text-sm text-gray-300">مالك المتجر</p>
                  </div>
                </div>

                {selectedStore.owner.phone_number && (
                  <>
                    <a
                      href={`tel:${selectedStore.owner.phone_number}`}
                      className="flex items-center gap-3 p-3 bg-white bg-opacity-10 rounded-xl hover:bg-opacity-20 transition-all duration-200"
                    >
                      <div className="flex-shrink-0">
                        <div className="w-10 h-10 rounded-full bg-white bg-opacity-20 flex items-center justify-center">
                          <span className="text-lg text-[var(--secondary-color)]">📞</span>
                        </div>
                      </div>
                      <div>
                        <p className="font-medium text-[var(--secondary-color)]">{selectedStore.owner.phone_number}</p>
                        <p className="text-sm text-gray-300">اتصال هاتفي</p>
                      </div>
                    </a>

                    <a
                      href={`https://wa.me/${selectedStore.owner.phone_number.replace(/[^0-9]/g, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 p-3 bg-green-600 hover:bg-green-700 rounded-xl transition-all duration-200"
                    >
                      <div className="flex-shrink-0">
                        <div className="w-10 h-10 rounded-full bg-white bg-opacity-20 flex items-center justify-center">
                          <span className="text-lg text-[var(--secondary-color)]">💬</span>
                        </div>
                      </div>
                      <div>
                        <p className="font-medium text-white">التواصل عبر الواتساب</p>
                        <p className="text-sm text-gray-200">رد سريع</p>
                      </div>
                    </a>
                  </>
                )}

                <a
                  href={`mailto:${selectedStore.owner.email}`}
                  className="flex items-center gap-3 p-3 bg-white bg-opacity-10 rounded-xl hover:bg-opacity-20 transition-all duration-200"
                >
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 rounded-full bg-white bg-opacity-20 flex items-center justify-center">
                      <span className="text-lg text-[var(--secondary-color)]">📧</span>
                    </div>
                  </div>
                  <div>
                    <p className="font-medium truncate text-[var(--secondary-color)]">{selectedStore.owner.email}</p>
                    <p className="text-sm text-gray-300">بريد إلكتروني</p>
                  </div>
                </a>
              </div>
            </div>

            {/* Branches */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold mb-4 text-center sm:text-left text-[var(--secondary-color)]">فروع المتجر</h3>
              
              <div className="space-y-3 max-h-60 overflow-y-auto pr-2">
                {selectedStore.branches.length > 0 ? (
                  selectedStore.branches.map((branch) => (
                    <div 
                      key={branch.id} 
                      className="p-3 bg-white bg-opacity-10 rounded-xl hover:bg-opacity-20 transition-all duration-200"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-8 rounded-full bg-white bg-opacity-20 flex items-center justify-center">
                          <span className="text-sm text-[var(--secondary-color)]">🏪</span>
                        </div>
                        <p className="font-medium text-[var(--secondary-color)]">{branch.name}</p>
                      </div>
                      
                      {branch.manager?.phone_number && (
                        <div className="flex items-center gap-2 text-sm text-gray-300">
                          <span className="text-[var(--secondary-color)]">📞</span>
                          <a 
                            href={`tel:${branch.manager.phone_number}`}
                            className="hover:text-[var(--secondary-color)] hover:underline transition-colors text-gray-300"
                          >
                            {branch.manager.phone_number}
                          </a>
                          {branch.manager.first_name && (
                            <span className="text-xs text-gray-400">({branch.manager.first_name})</span>
                          )}
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="p-4 bg-white bg-opacity-10 rounded-xl text-center">
                    <p className="text-gray-300">لا توجد فروع مسجلة بعد.</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Developer Credit */}
          <div className="mt-10 pt-6 border-t border-white border-opacity-20">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <p className="text-sm text-gray-300">
                © {new Date().getFullYear()} {selectedStore.name}. جميع الحقوق محفوظة.
              </p>
              
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                  <span className="text-xs text-[var(--secondary-color)]">❤️</span>
                </div>
                <p className="text-sm text-gray-300">
                  برمجة وتطوير بواسطة <span className="text-blue-300 font-medium">CODEOCEAN</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
};

export default StoreLayout;